/**
 * QikTruck Partner API - external integrations for ERP, TMS, and custom backends.
 * Auth: provisioned API keys on /v1/partner/* routes.
 */

export const API_BASE = "https://api.qiktruck.co.za";
export const PARTNER_API_PREFIX = "/v1/partner";
export const DOCS_PATH = "/api-docs?product=partner";
export const INTEGRATIONS_PATH = "/integrations?type=partner";
export const PARTNER_PLANS_PATH = "/partner-plans";

export const NAV_GROUPS = [
  {
    label: "Get started",
    items: [
      { id: "overview", label: "Overview" },
      { id: "compare", label: "Widget vs API" },
      { id: "request-access", label: "Request access" },
      { id: "plans", label: "Plans & pricing" },
    ],
  },
  {
    label: "How it works",
    items: [
      { id: "vehicle-types", label: "Vehicle catalog" },
      { id: "quoting", label: "Create quote" },
      { id: "booking", label: "Book a trip" },
      { id: "orders", label: "Track & manage" },
      { id: "billing", label: "Billing" },
    ],
  },
  {
    label: "Developer reference",
    items: [
      { id: "auth", label: "Authentication" },
      { id: "measurements", label: "Units & formats" },
      { id: "field-reference", label: "Field reference" },
      { id: "reference", label: "All endpoints" },
      { id: "errors", label: "Errors" },
    ],
  },
];

export const NAV = NAV_GROUPS.flatMap((g) => g.items);

export const MEASUREMENTS = [
  { title: "Price (sellingprice)", unit: "South African cents", example: "12500 = R125.00" },
  { title: "Distance (tripdistance)", unit: "Metres", example: "64500 = 64.5 km" },
  { title: "Duration (tripduration)", unit: "Seconds", example: "300 = 5 minutes" },
];

const PARTNER_ORDER_EXAMPLE = `{
  "orderid": "171f420f-3bda-42a8-a1c2-8ed245044297",
  "quoteid": "8e28cac4-95b8-4433-807b-4deb7d59e9f0",
  "status": "in-review",
  "trucktype": "1.5tonopen",
  "description": "Office furniture delivery",
  "category": "handheld-items",
  "collectiondate": "2026-06-25",
  "collectiontime": "11:00",
  "collection": {
    "lat": -26.111657,
    "lng": 27.9903441,
    "address": "Randburg, Gauteng"
  },
  "delivery": {
    "lat": -25.7478676,
    "lng": 28.2292712,
    "address": "Pretoria, Gauteng"
  },
  "tripdistance": 64508,
  "tripduration": 3022,
  "sellingprice": 295000,
  "contact": {
    "firstname": "sam",
    "lastname": "customer",
    "email": "customer@example.com",
    "mobilenumber": "0821234567"
  },
  "driver": null,
  "created_at": "2026-06-24T09:15:00.000Z"
}`;

/** Documented partner surface - maps to dedicated /v1/partner/* handlers (API key gated). */
export const ENDPOINTS = {
  vehicleTypes: {
    method: "GET",
    path: "/v1/partner/vehicle-types",
    auth: "API key",
    summary: "Catalog of bookable vehicles and load categories. Call this first to discover valid trucktype and category values for POST /quotes.",
    note: "Use vehicles[].type for trucktype. Pick category from vehicles[].categories (must be valid for that vehicle).",
    response: `{
  "configurations": ["open", "covered"],
  "categories": [
    { "type": "handheld-items", "description": "Documents, Food, Medicine." },
    { "type": "furniture", "description": "Home and office furniture movers." }
  ],
  "vehicles": [{
    "vehicletype": "1.5tonopen",
    "type": "1.5tonopen",
    "description": "open bakkie truck",
    "displaytype": "open bakkie truck",
    "bodylabel": "open body",
    "loadrange": "1.5to3tonrange",
    "tonnes": 1.5,
    "categories": ["furniture", "equipment", "materials", "fresh-goods", "handheld-items"]
  }]
}`,
  },
  quote: {
    method: "POST",
    path: "/v1/partner/quotes",
    auth: "API key",
    summary: "Create a quotation for a trip. Quotes are scoped to your partner customer account.",
    note: "Discover trucktype and category from GET /v1/partner/vehicle-types before calling this endpoint. Save quoteid from the response for POST /quotes/{quoteid}/accept.",
    body: `{
  "description": "Office desk and chairs",
  "trucktype": "1.5tonopen",
  "collection": { "lat": -26.111657, "lng": 27.9903441 },
  "delivery": { "lat": -25.7478676, "lng": 28.2292712 },
  "collectiondate": "2026-06-25",
  "collectiontime": "11:00",
  "category": "handheld-items",
  "runners": 1,
  "floors": 0,
  "promocode": "",
  "contact": {
    "email": "customer@example.com",
    "mobilenumber": "0821234567",
    "firstname": "Sam",
    "lastname": "Customer"
  }
}`,
    response: `{
  "quoteid": "8e28cac4-95b8-4433-807b-4deb7d59e9f0",
  "trucktype": "1.5tonopen",
  "tripdistance": 64508,
  "tripduration": 3022,
  "sellingprice": 295000,
  "discount": 0
}`,
  },
  acceptQuote: {
    method: "POST",
    path: "/v1/partner/quotes/{quoteid}/accept",
    auth: "API key",
    summary: "Book a trip from a priced quote. Creates an order. The quoteid is in the URL path.",
    note: "email and mobilenumber are required unless already stored on the quote from POST /quotes. payment_payer: merchant (default) bills your QikTruck account; use end_customer to return a PayFast link for your shopper. return_url and cancel_url are optional HTTPS redirects after PayFast (defaults to qiktruck.co.za/track-order).",
    body: `{
  "email": "customer@example.com",
  "mobilenumber": "0821234567",
  "firstname": "Sam",
  "lastname": "Customer",
  "collectiondate": "2026-06-25",
  "collectiontime": "11:00",
  "description": "Office desk and chairs",
  "payment_payer": "merchant",
  "return_url": "https://yourstore.co.za/checkout/delivery/success",
  "cancel_url": "https://yourstore.co.za/checkout/delivery/cancelled"
}`,
    response: `{
  "orderid": "171f420f-3bda-42a8-a1c2-8ed245044297",
  "quoteid": "8e28cac4-95b8-4433-807b-4deb7d59e9f0",
  "status": "in-review",
  "trucktype": "1.5tonopen",
  "sellingprice": 295000,
  "tripdistance": 64508,
  "tripduration": 3022,
  "vehicleid": null,
  "payment_payer": "end_customer",
  "payment": {
    "payer": "end_customer",
    "amount_cents": 295000,
    "payment_page_url": "https://qiktruck.co.za/customermobileapppayment.html?...",
    "return_url": "https://qiktruck.co.za/track-order?success=true&paynow=yes&...",
    "cancel_url": "https://qiktruck.co.za/track-order?cancel=true&paynow=yes&..."
  }
}`,
  },
  orderPayment: {
    method: "POST",
    path: "/v1/partner/orders/{orderid}/payment",
    auth: "API key",
    summary: "Regenerate a PayFast payment page for an end-customer-paid trip that is not yet paid.",
    body: `{
  "return_url": "https://yourstore.co.za/checkout/delivery/success",
  "cancel_url": "https://yourstore.co.za/checkout/delivery/cancelled"
}`,
    response: `{
  "orderid": "171f420f-3bda-42a8-a1c2-8ed245044297",
  "payment_payer": "end_customer",
  "payment": {
    "payer": "end_customer",
    "amount_cents": 295000,
    "payment_page_url": "https://qiktruck.co.za/customermobileapppayment.html?..."
  }
}`,
  },
  trackOrder: {
    method: "POST",
    path: "/v1/partner/orders/{orderid}/track",
    auth: "API key",
    summary: "Track a single order by id. The orderid is in the URL; body is optional.",
    body: `{}`,
    response: PARTNER_ORDER_EXAMPLE,
  },
  listOrders: {
    method: "GET",
    path: "/v1/partner/orders",
    auth: "API key",
    summary: "List up to 200 recent orders for your partner account (newest first).",
    response: `{
  "count": 1,
  "orders": [${PARTNER_ORDER_EXAMPLE.replace(/\n/g, "\n    ")}]
}`,
  },
  cancelOrder: {
    method: "POST",
    path: "/v1/partner/orders/{orderid}/cancel",
    auth: "API key",
    summary: "Cancel while status is in-review, accepted, or in-progress. Not allowed once finished, completed, stopped, or cancelled.",
    body: `{ "reason": "Customer changed mind" }`,
    response: `{
  "orderid": "171f420f-3bda-42a8-a1c2-8ed245044297",
  "status": "stopped"
}`,
  },
  updateContacts: {
    method: "POST",
    path: "/v1/partner/orders/{orderid}/contacts",
    auth: "API key",
    summary: "Update end-customer or collection details on an existing order. Only sent fields are updated.",
    body: `{
  "firstname": "Sam",
  "lastname": "Customer",
  "email": "customer@example.com",
  "mobilenumber": "0821234567",
  "collectiondate": "2026-06-26",
  "collectiontime": "14:00",
  "description": "Updated delivery notes",
  "category": "handheld-items"
}`,
    response: `{
  "orderid": "171f420f-3bda-42a8-a1c2-8ed245044297",
  "updated": true
}`,
  },
  apiRegistration: {
    method: "POST",
    path: "/api-registrations",
    auth: "Public",
    summary: "Request Partner API access. QikTruck provisions API keys after review.",
    body: `{
  "email": "dev@yourcompany.co.za",
  "description": "ERP integration - 200 daily deliveries across Gauteng",
  "apitype": "enterprise",
  "application": {
    "contact_name": "Jane Dlamini",
    "contact_job_title": "Head of Operations",
    "contact_phone": "+27821234567",
    "technical_contact_email": "dev@yourcompany.co.za",
    "company_legal_name": "Acme Logistics (Pty) Ltd",
    "company_registration_number": "2019/123456/07",
    "company_registered_address": "12 Industrial Rd, Spartan, Kempton Park",
    "company_city": "Johannesburg",
    "company_province": "Gauteng",
    "company_postal_code": "1619",
    "company_country": "South Africa",
    "website_url": "https://acme.co.za",
    "domains": ["acme.co.za", "shop.acme.co.za"],
    "social_linkedin": "https://linkedin.com/company/acme",
    "integration_category": "erp",
    "planned_go_live_date": "2026-09-01",
    "terms_partner_api": true,
    "terms_not_competitor": true,
    "terms_authorized_signatory": true
  },
  "source_": "customer-hq:integrations"
}`,
    response: `true`,
  },
};

export const API_CATALOG = [
  { area: "Vehicles", routes: "GET /v1/partner/vehicle-types" },
  { area: "Quoting", routes: "POST /v1/partner/quotes" },
  { area: "Booking", routes: "POST /v1/partner/quotes/{id}/accept" },
  { area: "Payment", routes: "POST /v1/partner/orders/{id}/payment" },
  { area: "Tracking", routes: "POST /v1/partner/orders/{id}/track · GET /v1/partner/orders" },
  { area: "Changes", routes: "POST /v1/partner/orders/{id}/cancel · POST .../contacts" },
];

/** Plain-language journey for business readers. */
export const PARTNER_FLOW_PLAIN = [
  { title: "Apply", detail: "Share your company details. No coding needed." },
  { title: "Get access", detail: "After review, we email you when Partner API access is active." },
  { title: "Create keys", detail: "Sign in and create sandbox keys in Integrations. Production keys unlock after go-live review." },
  { title: "Vehicle catalog", detail: "GET /vehicle-types returns bookable trucktype and category slugs for your integration." },
  { title: "Create quote", detail: "POST /quotes with those slugs plus pickup, delivery, and schedule to get a price and quoteid." },
  { title: "Book", detail: "Confirm the quote to dispatch a driver." },
  { title: "Track", detail: "Check status until the trip is complete." },
];

export const PARTNER_FLOW = [
  "Request Partner API access in Integrations",
  "Create sandbox API keys once access is active",
  "GET /v1/partner/vehicle-types",
  "POST /v1/partner/quotes → review price",
  "POST /v1/partner/quotes/{id}/accept → book",
  "Track via GET /v1/partner/orders or POST .../track",
];

/** @deprecated Use PARTNER_FLOW - kept for URL aliases */
export const ENTERPRISE_FLOW = PARTNER_FLOW;

/** Load categories - always confirm vehicles[].categories for the trucktype you chose. */
export const LOAD_CATEGORIES = [
  { slug: "handheld-items", description: "Documents, parcels, medicine, small packages." },
  { slug: "furniture", description: "Home and office furniture." },
  { slug: "equipment", description: "Construction, manufacturing, and office equipment." },
  { slug: "materials", description: "Building and industrial materials." },
  { slug: "fresh-goods", description: "Food, groceries, flowers, FMCG." },
];

/**
 * Typical bookable slugs (sell: true). Always prefer GET /v1/partner/vehicle-types - availability changes.
 * Slug pattern: {tonnage}ton{configuration} e.g. 1.5tonopen, 4tonclosedfridge; minivanopen for mini bakkies.
 */
export const BOOKABLE_VEHICLE_SLUGS = [
  { slug: "minivanopen", label: "Open mini bakkie", tonnes: 0.8, configurations: "open" },
  { slug: "1tonopen", label: "Open 1 ton bakkie", tonnes: 1, configurations: "open" },
  { slug: "1.5tonopen", label: "Open 1.5 ton bakkie", tonnes: 1.5, configurations: "open" },
  { slug: "4tonopen", label: "Open 4 ton truck", tonnes: 4, configurations: "open" },
  { slug: "8tonopen", label: "Open 8 ton truck", tonnes: 8, configurations: "open" },
  { slug: "16tonopen", label: "Open 16 ton truck", tonnes: 16, configurations: "open" },
];

export const VEHICLE_TYPE_CODE_EXAMPLE = `// 1) Fetch catalog (cache 5 minutes - same Cache-Control as the API)
const res = await fetch("${API_BASE}/v1/partner/vehicle-types", {
  headers: { Authorization: "Bearer qt_test_YOUR_KEY" },
});
const catalog = await res.json();

// 2) Pick trucktype (slug) - use vehicles[].type or vehicles[].vehicletype
const trucktype = catalog.vehicles.find((v) => v.tonnes >= 1.5)?.type; // e.g. "1.5tonopen"

// 3) Pick category supported by that vehicle
const category = catalog.vehicles
  .find((v) => v.type === trucktype)
  ?.categories?.includes("furniture")
  ? "furniture"
  : "handheld-items";

// 4) POST /v1/partner/quotes with trucktype + category + coordinates + schedule`;

export const TRUCKTYPE_VALIDATION_NOTES = [
  "trucktype must exactly match vehicles[].type from GET /v1/partner/vehicle-types (case-sensitive, lowercase).",
  "Only bookable vehicles are returned in vehicles[] (filtered by your plan tier).",
  "category must be one of the strings in that vehicle's categories array.",
  "Invalid trucktype or category typically returns HTTP 400 with a short message from the pricing engine.",
  "Do not parse tonnage from the slug in your app - use vehicles[].tonnes and displaytype for UI labels.",
];

/** Public plan tiers - prices also served from GET /partner/plans */
export const PARTNER_PLANS = [
  {
    id: "sandbox",
    headline: "Sandbox",
    price_label: "Free",
    billing: "Included after HQ activates access",
    highlights: ["qt_test_ keys", "100 quotes / 10 bookings per month (sandbox cap)", "Lite vehicles (≤4 ton)"],
  },
  {
    id: "starter",
    headline: "Starter",
    price_label: "R499 / month",
    billing: "PayFast recurring subscription",
    highlights: ["500 quotes / 50 bookings", "Standard vehicles (≤8 ton)", "Production keys after go-live"],
  },
  {
    id: "operations",
    headline: "Operations",
    price_label: "R2 499 / month + R15/booking",
    billing: "PayFast subscription + metered platform fee",
    highlights: ["5 000 quotes / 500 bookings", "Full catalog", "Webhooks"],
  },
  {
    id: "enterprise",
    headline: "Enterprise",
    price_label: "Custom",
    billing: "Invoice / contract",
    highlights: ["Unlimited fair use", "Custom SLAs", "Contact QikTruck sales"],
  },
];

export const ACCEPT_PACKS_DOCS = [
  { id: "accept_25", label: "25 extra bookings", price: "R499", note: "One-off PayFast - current calendar month only" },
  { id: "accept_100", label: "100 extra bookings", price: "R1 499", note: "Best value near monthly limit" },
];

/** How developers discover enum-like values (trucktype, category). */
export const QUOTE_LOOKUP_WORKFLOW = [
  {
    step: "1",
    title: "GET /v1/partner/vehicle-types",
    detail: "Fetch the live catalog. Do not hard-code vehicle slugs - availability and pricing change over time.",
  },
  {
    step: "2",
    title: "Choose trucktype",
    detail: "Pick vehicles[].type (alias of vehicletype). Example slugs: minivanopen, 1tonopen, 1.5tonopen, 4tonopen, 8tonopen, 16tonopen.",
  },
  {
    step: "3",
    title: "Choose category",
    detail: "Use categories[].type (e.g. handheld-items, furniture, materials). The value must appear in the chosen vehicle's categories array.",
  },
  {
    step: "4",
    title: "POST /v1/partner/quotes",
    detail: "Send trucktype, category, coordinates, schedule, and description. Store quoteid from the response for booking.",
  },
];

/** Vehicle catalog only - see Create quote for POST /quotes. */
export const VEHICLE_CATALOG_WORKFLOW = QUOTE_LOOKUP_WORKFLOW.slice(0, 3);

/** Quote pricing only - requires trucktype + category from the vehicle catalog. */
export const QUOTE_WORKFLOW = [
  {
    title: "Prerequisites",
    detail: "Resolve trucktype and category from GET /v1/partner/vehicle-types first. Do not guess slugs.",
  },
  {
    title: "POST /v1/partner/quotes",
    detail: "Send trucktype, category, collection and delivery coordinates, schedule, and load description.",
  },
  {
    title: "Store quoteid",
    detail: "Save quoteid and sellingprice from the response. You need quoteid to book the trip (POST /quotes/{quoteid}/accept).",
  },
];

export const VEHICLE_TYPES_RESPONSE_FIELDS = [
  {
    field: "configurations",
    type: "string[]",
    required: true,
    description: "Body configuration labels used across the fleet (e.g. open, covered).",
    example: '["open", "covered"]',
  },
  {
    field: "categories",
    type: "object[]",
    required: true,
    description: "Load types you can pass as category on POST /quotes.",
    example: '{ "type": "handheld-items", "description": "..." }',
  },
  {
    field: "categories[].type",
    type: "string",
    required: true,
    description: "Category slug for POST /quotes.category.",
    example: "handheld-items",
    nested: true,
  },
  {
    field: "vehicles",
    type: "object[]",
    required: true,
    description: "Bookable vehicle definitions for your plan tier (no internal pricing fields).",
    example: '[{ "vehicletype": "1.5tonopen", "type": "1.5tonopen", "displaytype": "open bakkie truck", "tonnes": 1.5, "categories": ["furniture"] }]',
  },
  {
    field: "vehicles[].type",
    type: "string",
    required: true,
    description: "Vehicle slug for POST /quotes.trucktype. Same value as vehicletype.",
    example: "1.5tonopen",
    nested: true,
  },
  {
    field: "vehicles[].vehicletype",
    type: "string",
    required: true,
    description: "Canonical vehicle identifier (duplicate of type).",
    example: "1.5tonopen",
    nested: true,
  },
  {
    field: "vehicles[].description",
    type: "string",
    required: true,
    description: "Human-readable label for UI (same as displaytype; not sent in API requests).",
    example: "open bakkie truck",
    nested: true,
  },
  {
    field: "vehicles[].displaytype",
    type: "string",
    required: true,
    description: "Human-readable vehicle label for UI.",
    example: "open bakkie truck",
    nested: true,
  },
  {
    field: "vehicles[].bodylabel",
    type: "string",
    required: false,
    description: "Body style label (e.g. open body, closed body, fridge body).",
    example: "open body",
    nested: true,
  },
  {
    field: "vehicles[].loadrange",
    type: "string",
    required: false,
    description: "Fleet grouping slug for UI filters (e.g. 1tonrange, 1.5to3tonrange).",
    example: "1.5to3tonrange",
    nested: true,
  },
  {
    field: "vehicles[].categories",
    type: "string[]",
    required: true,
    description: "Category slugs valid for this vehicle on POST /quotes.category.",
    example: '["furniture", "handheld-items"]',
    nested: true,
  },
  {
    field: "vehicles[].tonnes",
    type: "number",
    required: true,
    description: "Approximate payload capacity in metric tonnes.",
    example: "1.5",
    nested: true,
  },
];

export const QUOTE_REQUEST_FIELDS = [
  {
    field: "trucktype",
    type: "string",
    required: true,
    description: "Vehicle slug from GET /v1/partner/vehicle-types (vehicles[].type). Case-sensitive, lowercase.",
    example: "1.5tonopen",
  },
  {
    field: "category",
    type: "string",
    required: true,
    description: "Load category slug from vehicle-types. Must appear in the chosen vehicle's categories array.",
    example: "handheld-items",
  },
  {
    field: "description",
    type: "string",
    required: true,
    description: "Free-text cargo or job description shown to operations and the driver.",
    example: "Office desk and 4 chairs",
  },
  {
    field: "collection",
    type: "object",
    required: true,
    description: "Pickup coordinates (WGS-84).",
    example: '{ "lat": -26.111657, "lng": 27.9903441 }',
  },
  {
    field: "collection.lat",
    type: "number",
    required: true,
    description: "Pickup latitude (decimal degrees, negative for Southern Hemisphere).",
    example: "-26.111657",
    nested: true,
  },
  {
    field: "collection.lng",
    type: "number",
    required: true,
    description: "Pickup longitude (decimal degrees).",
    example: "27.9903441",
    nested: true,
  },
  {
    field: "delivery",
    type: "object",
    required: true,
    description: "Drop-off coordinates (WGS-84).",
    example: '{ "lat": -25.7478676, "lng": 28.2292712 }',
  },
  {
    field: "delivery.lat",
    type: "number",
    required: true,
    description: "Delivery latitude.",
    example: "-25.7478676",
    nested: true,
  },
  {
    field: "delivery.lng",
    type: "number",
    required: true,
    description: "Delivery longitude.",
    example: "28.2292712",
    nested: true,
  },
  {
    field: "collectiondate",
    type: "string",
    required: true,
    description: "Requested pickup date.",
    example: "2026-06-25 (YYYY-MM-DD)",
  },
  {
    field: "collectiontime",
    type: "string",
    required: true,
    description: "Requested pickup time in 24-hour local (SAST) format.",
    example: "11:00",
  },
  {
    field: "contact",
    type: "object",
    required: "recommended",
    description: "End-customer details. Optional at quote time; required when booking if not sent here.",
    example: "See nested contact.* fields",
  },
  {
    field: "contact.email",
    type: "string",
    required: "conditional",
    description: "Customer email. Required when booking if omitted from the quote.",
    example: "customer@example.com",
    nested: true,
  },
  {
    field: "contact.mobilenumber",
    type: "string",
    required: "conditional",
    description: "South African mobile number. Required when booking if omitted from the quote.",
    example: "0821234567",
    nested: true,
  },
  {
    field: "contact.firstname",
    type: "string",
    required: false,
    description: "Customer first name.",
    example: "Sam",
    nested: true,
  },
  {
    field: "contact.lastname",
    type: "string",
    required: false,
    description: "Customer last name.",
    example: "Customer",
    nested: true,
  },
  {
    field: "runners",
    type: "integer",
    required: false,
    description: "Number of off-truck helpers (runners) requested. Affects price.",
    example: "0 (default)",
  },
  {
    field: "floors",
    type: "integer",
    required: false,
    description: "Number of floors at delivery (stairs / lift access). Affects price.",
    example: "0 (default)",
  },
  {
    field: "promocode",
    type: "string",
    required: false,
    description: "Active QikTruck promo code. Empty string if none.",
    example: '"" or "SUMMER10"',
  },
];

export const QUOTE_RESPONSE_FIELDS = [
  {
    field: "quoteid",
    type: "string (UUID)",
    required: true,
    description: "Quote reference. Pass to POST /v1/partner/quotes/{quoteid}/accept.",
    example: "8e28cac4-95b8-4433-807b-4deb7d59e9f0",
  },
  {
    field: "trucktype",
    type: "string",
    required: true,
    description: "Echo of the vehicle slug used for pricing.",
    example: "1.5tonopen",
  },
  {
    field: "tripdistance",
    type: "integer",
    required: true,
    description: "Driving distance in metres.",
    example: "64508 (= 64.5 km)",
  },
  {
    field: "tripduration",
    type: "integer",
    required: true,
    description: "Estimated driving time in seconds (excludes loading).",
    example: "3022 (= ~50 min)",
  },
  {
    field: "sellingprice",
    type: "integer",
    required: true,
    description: "Quoted price in South African cents (ZAR). Divide by 100 for rands.",
    example: "295000 (= R2,950.00)",
  },
  {
    field: "discount",
    type: "integer",
    required: true,
    description: "Promo discount applied, in cents. 0 when no promo.",
    example: "0",
  },
];

export const ACCEPT_RESPONSE_FIELDS = [
  {
    field: "orderid",
    type: "string (UUID)",
    required: true,
    description: "Booked trip id. Use for track, cancel, contacts, and payment routes.",
    example: "171f420f-3bda-42a8-a1c2-8ed245044297",
  },
  {
    field: "quoteid",
    type: "string (UUID)",
    required: true,
    description: "Quote that was booked.",
    example: "8e28cac4-95b8-4433-807b-4deb7d59e9f0",
  },
  {
    field: "status",
    type: "string",
    required: true,
    description: "Initial order status. Usually in-review while a driver is assigned.",
    example: "in-review",
  },
  {
    field: "sellingprice",
    type: "integer",
    required: true,
    description: "Final booked price in South African cents.",
    example: "295000",
  },
  {
    field: "tripdistance",
    type: "integer",
    required: true,
    description: "Distance in metres.",
    example: "64508",
  },
  {
    field: "tripduration",
    type: "integer",
    required: true,
    description: "Estimated duration in seconds.",
    example: "3022",
  },
  {
    field: "payment_payer",
    type: "string",
    required: false,
    description: "Who pays QikTruck: merchant or end_customer.",
    example: "merchant",
  },
  {
    field: "payment",
    type: "object",
    required: false,
    description: "Present when payment_payer is end_customer. Send the shopper to payment.payment_page_url.",
    example: "See payment.* fields",
  },
  {
    field: "payment.payer",
    type: "string",
    required: false,
    description: "Always end_customer when payment object is returned.",
    example: "end_customer",
    nested: true,
  },
  {
    field: "payment.amount_cents",
    type: "integer",
    required: false,
    description: "Amount due on PayFast, in cents.",
    example: "295000",
    nested: true,
  },
  {
    field: "payment.payment_page_url",
    type: "string (HTTPS URL)",
    required: false,
    description: "QikTruck-hosted page that redirects to PayFast. Open in browser or redirect shopper.",
    example: "https://qiktruck.co.za/customermobileapppayment.html?...",
    nested: true,
  },
];

export const ORDER_OBJECT_FIELDS = [
  {
    field: "orderid",
    type: "string (UUID)",
    required: true,
    description: "Trip identifier.",
    example: "171f420f-3bda-42a8-a1c2-8ed245044297",
  },
  {
    field: "quoteid",
    type: "string (UUID)",
    required: true,
    description: "Source quote.",
    example: "8e28cac4-95b8-4433-807b-4deb7d59e9f0",
  },
  {
    field: "status",
    type: "string",
    required: true,
    description: "Lifecycle status. See order status section.",
    example: "in-progress",
  },
  {
    field: "trucktype",
    type: "string",
    required: true,
    description: "Vehicle slug used for the trip.",
    example: "1.5tonopen",
  },
  {
    field: "category",
    type: "string",
    required: true,
    description: "Load category slug.",
    example: "furniture",
  },
  {
    field: "description",
    type: "string",
    required: false,
    description: "Cargo / job notes.",
    example: "Office furniture delivery",
  },
  {
    field: "collectiondate",
    type: "string",
    required: true,
    description: "Scheduled pickup date (YYYY-MM-DD).",
    example: "2026-06-25",
  },
  {
    field: "collectiontime",
    type: "string",
    required: true,
    description: "Scheduled pickup time (24h, SAST).",
    example: "11:00",
  },
  {
    field: "collection",
    type: "object",
    required: true,
    description: "Pickup location.",
    example: '{ "lat": -26.11, "lng": 27.99, "address": "..." }',
  },
  {
    field: "delivery",
    type: "object",
    required: true,
    description: "Drop-off location.",
    example: '{ "lat": -25.75, "lng": 28.23, "address": "..." }',
  },
  {
    field: "tripdistance",
    type: "integer",
    required: true,
    description: "Metres.",
    example: "64508",
  },
  {
    field: "tripduration",
    type: "integer",
    required: true,
    description: "Seconds.",
    example: "3022",
  },
  {
    field: "sellingprice",
    type: "integer",
    required: true,
    description: "Price in cents.",
    example: "295000",
  },
  {
    field: "contact",
    type: "object",
    required: true,
    description: "End-customer contact on the trip.",
    example: '{ "firstname", "lastname", "email", "mobilenumber" }',
  },
  {
    field: "driver",
    type: "object | null",
    required: false,
    description: "Assigned driver when available; null while unassigned.",
    example: '{ "firstname", "mobilenumber", "vehicleid", ... }',
  },
  {
    field: "created_at",
    type: "string (ISO 8601)",
    required: true,
    description: "UTC timestamp when the order was created.",
    example: "2026-06-24T09:15:00.000Z",
  },
];

export const CANCEL_REQUEST_FIELDS = [
  {
    field: "reason",
    type: "string",
    required: false,
    description: "Optional cancellation reason for operations.",
    example: "Customer changed mind",
  },
];

export const CONTACTS_REQUEST_FIELDS = [
  {
    field: "firstname",
    type: "string",
    required: false,
    description: "Update customer first name.",
    example: "Sam",
  },
  {
    field: "lastname",
    type: "string",
    required: false,
    description: "Update customer last name.",
    example: "Customer",
  },
  {
    field: "email",
    type: "string",
    required: false,
    description: "Update customer email.",
    example: "customer@example.com",
  },
  {
    field: "mobilenumber",
    type: "string",
    required: false,
    description: "Update customer mobile.",
    example: "0821234567",
  },
  {
    field: "collectiondate",
    type: "string",
    required: false,
    description: "Reschedule pickup date.",
    example: "2026-06-26",
  },
  {
    field: "collectiontime",
    type: "string",
    required: false,
    description: "Reschedule pickup time.",
    example: "14:00",
  },
  {
    field: "description",
    type: "string",
    required: false,
    description: "Update cargo notes.",
    example: "Fragile items",
  },
  {
    field: "category",
    type: "string",
    required: false,
    description: "Change load category (must remain valid for trucktype).",
    example: "handheld-items",
  },
];

export const ACCEPT_REQUEST_FIELDS = [
  {
    field: "email",
    type: "string",
    required: "conditional",
    description: "End-customer email. Required unless already stored on the quote (via contact.email).",
    example: "customer@example.com",
  },
  {
    field: "mobilenumber",
    type: "string",
    required: "conditional",
    description: "End-customer mobile. Required unless already on the quote.",
    example: "0821234567",
  },
  {
    field: "firstname",
    type: "string",
    required: false,
    description: "Customer first name.",
    example: "Sam",
  },
  {
    field: "lastname",
    type: "string",
    required: false,
    description: "Customer last name.",
    example: "Customer",
  },
  {
    field: "collectiondate",
    type: "string",
    required: false,
    description: "Override pickup date from the quote.",
    example: "2026-06-25",
  },
  {
    field: "collectiontime",
    type: "string",
    required: false,
    description: "Override pickup time from the quote.",
    example: "11:00",
  },
  {
    field: "description",
    type: "string",
    required: false,
    description: "Override cargo description from the quote.",
    example: "Updated delivery notes",
  },
  {
    field: "payment_payer",
    type: "string",
    required: false,
    description: "Who pays QikTruck for this trip.",
    example: "merchant (default) or end_customer",
  },
  {
    field: "return_url",
    type: "string (HTTPS URL)",
    required: false,
    description: "Redirect after successful PayFast payment. Only when payment_payer is end_customer.",
    example: "https://yourstore.co.za/checkout/success",
  },
  {
    field: "cancel_url",
    type: "string (HTTPS URL)",
    required: false,
    description: "Redirect if the shopper cancels PayFast.",
    example: "https://yourstore.co.za/checkout/cancelled",
  },
];

/** Grouped field tables for the Field reference nav tab. */
export const FIELD_REFERENCE_SECTIONS = [
  { id: "vehicle-types", title: "GET /v1/partner/vehicle-types - response", fields: VEHICLE_TYPES_RESPONSE_FIELDS },
  { id: "quote-request", title: "POST /v1/partner/quotes - request", fields: QUOTE_REQUEST_FIELDS },
  { id: "quote-response", title: "POST /v1/partner/quotes - response", fields: QUOTE_RESPONSE_FIELDS },
  { id: "accept-request", title: "POST /v1/partner/quotes/{id}/accept - request", fields: ACCEPT_REQUEST_FIELDS },
  { id: "accept-response", title: "POST /v1/partner/quotes/{id}/accept - response", fields: ACCEPT_RESPONSE_FIELDS },
  { id: "order-object", title: "Order object (track / list)", fields: ORDER_OBJECT_FIELDS },
  { id: "cancel", title: "POST /v1/partner/orders/{id}/cancel - request", fields: CANCEL_REQUEST_FIELDS },
  { id: "contacts", title: "POST /v1/partner/orders/{id}/contacts - request", fields: CONTACTS_REQUEST_FIELDS },
];

/** @deprecated Use QUOTE_REQUEST_FIELDS - kept for enterprise URL re-exports */
export const QUOTE_FIELDS = QUOTE_REQUEST_FIELDS.map((f) => [
  f.field,
  f.description,
  f.required === true ? "Required" : f.required === "recommended" ? "Recommended" : f.required === "conditional" ? "Required when booking" : "Optional",
]);

/** @deprecated Use ACCEPT_REQUEST_FIELDS */
export const ACCEPT_FIELDS = ACCEPT_REQUEST_FIELDS.map((f) => [
  f.field,
  f.description,
  f.required === true ? "Required" : f.required === "conditional" ? "Required if not on quote" : "Optional",
]);

/** Happy-path progression after POST /quotes/{id}/accept. Poll track or list until terminal. */
export const ORDER_STATUS_FLOW = [
  "in-review",
  "accepted",
  "in-progress",
  "finished",
];

export const ORDER_STATUSES = [
  {
    code: "in-review",
    plain: "Preparing your trip",
    detail: "QikTruck is reviewing the booking and assigning a driver.",
  },
  {
    code: "accepted",
    plain: "Driver confirmed",
    detail: "A driver accepted the job. Driver details appear when you track the order.",
  },
  {
    code: "in-progress",
    plain: "On the way",
    detail: "The driver is en route or completing the delivery.",
  },
  {
    code: "finished",
    plain: "Delivered",
    detail: "Trip completed successfully.",
  },
  {
    code: "completed",
    plain: "Delivered (alt.)",
    detail: "Same meaning as finished on some older orders.",
  },
  {
    code: "stopped",
    plain: "Cancelled",
    detail: "Cancelled via the Partner API or operations.",
  },
  {
    code: "cancelled",
    plain: "Cancelled",
    detail: "Cancelled through another QikTruck workflow.",
  },
  {
    code: "declined",
    plain: "Driver declined",
    detail: "The assigned driver declined. QikTruck may reassign or cancel.",
  },
];

export const ERROR_CODES = [
  ["401", "Missing or invalid API key (Authorization: Bearer qt_live_… or qt_test_…)."],
  ["403", "Key not authorised for this environment or route."],
  ["404", "Quote or order not found for your partner account."],
  ["400", "Validation error - check payload fields and required contact details."],
  ["402", "Prepaid account has unpaid finished trips. Settle via Billing before booking new trips."],
  ["502", "Upstream temporarily unavailable - retry with backoff."],
  ["429", "Rate limit exceeded (general API limit - 300 requests per 15 min per IP)."],
];

export const BILLING_MODES = [
  {
    id: "prepaid_per_order",
    title: "Pay per trip (merchant account)",
    summary: "Each booked delivery is charged to your QikTruck business account after you confirm the booking.",
    who_pays: "Your company (the QikTruck account linked to your API key).",
    when: "After you book a trip. Settle via Billing in this dashboard or your agreed payment method.",
    end_customer: "Your customer does not pay QikTruck directly unless you pass payment_payer: end_customer when you book.",
  },
  {
    id: "postpaid_invoice",
    title: "Monthly invoice",
    summary: "For approved accounts: trips accumulate and QikTruck invoices your finance team on a schedule.",
    who_pays: "Your finance contact (set under Integrations → Billing).",
    when: "Trips are booked immediately; payment is collected on your invoice cycle, not at booking time.",
    end_customer: "Same as pay per trip unless you use end_customer PayFast on individual bookings.",
  },
];

export const PAYMENT_PAYERS = [
  {
    id: "merchant",
    title: "Merchant pays QikTruck",
    summary: "Default. The trip is billed to your business account (prepaid or postpaid).",
  },
  {
    id: "end_customer",
    title: "End customer pays via PayFast",
    summary:
      "When you book, the API returns payment.payment_page_url - a QikTruck-hosted page that sends your customer to PayFast. Redirect or link them there at checkout.",
  },
];

export const CLAIMS_INFO = {
  form_url:
    "https://docs.google.com/forms/d/1FT9g6jIt6-8ucW07-XDOvmheEhsrUbXSaXsoXtHbCjE/viewform",
  note: "Use our claims form for damage, loss, or disputes. Include your trip ID, photos, and contact details. The same form is used by QikTruck operations.",
};
